import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useToast } from "../../../services/utils";
import { getTableColumns } from "../../../services/rulesService";
import EnhancedQueryDialog from "../../../components/Dialogs/EnhancedQueryDialog";
import ConfirmDailog from "../../../components/Dialogs/ConfirmDailog";
import ResourceColumnsChips from "../../../components/Organisms/ResourceColumnsChips";
import AvailableVariables from "../../../components/Molecules/Variables/AvailableVariables";
import QueryTemplatesCard from "../../../components/common/QueryTemplatesCard";
import {
  GENERIC_EXTERNAL_QUERY_TEMPLATES,
  GENERIC_RESOURCE_QUERY_TEMPLATES,
} from "../../../constants/queryTemplates";
import SubQueryCard from "./SubQueryCard";
import SourceReferenceCard from "./SourceReferenceCard";
import MixedSqlEditor from "./MixedSqlEditor";
import MixedQueryV2Content from "./MixedQueryV2Content";
import DynamicSubQueryEditorDialog from "./DynamicSubQueryEditorDialog";
import {
  IMixedSubQueryV2,
  getColumnNamesForSource,
  buildResourceScopedColumns,
  buildExternalScopedColumns,
  buildMergeScopedColumns,
  buildMainQueryScopedColumns,
  MERGE_SUB_TYPES,
} from "./mixedQueryV2.utils";

interface Handlers {
  handleTypeChange: (index: number, value: string | null) => void;
  handleFieldChange: (index: number, field: string, value: any) => void;
  handleLinkedServiceChange: (index: number, field: string, value: any) => void;
  handleQueryChange: (index: number, value: string) => void;
  handleEditorFocus: (index: number) => void;
  handleEditorBlur: () => void;
  removeSubQuery: (index: number) => void;
  handleToggleActive: (index: number) => void;
  getDependencyDisplayLabel: (marker: string) => string;
  handleSetExternalColumns: (
    index: number,
    cols: { name: string; value: string }[],
  ) => void;
}

interface MixedQueryEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  queryName: string;
  setQueryName: (value: string) => void;
  queryError?: any;
  subQueries: IMixedSubQueryV2[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  columns: any;
  setColumns: React.Dispatch<React.SetStateAction<any>>;
  resourcesData: any[];
  linkedServicesData: any[];
  mainQuery: string;
  setMainQuery: (value: string) => void;
  handlers: Handlers;
  getSubQueryLabel: (item: IMixedSubQueryV2, idx: number) => string;
  onAddSource?: () => void;
  isReadOnly?: boolean;
  errors?: any;
  availableSubTypes?: string[];
}

// Full-screen editor (Figma "Query Editor - v2"): a left sidebar listing all
// data sources, a focused single-source editor in the main area, and the Report
// Query at the bottom. Reuses SubQueryCard so field behaviour is identical.
const MixedQueryEditorDialog: React.FC<MixedQueryEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  queryName,
  setQueryName,
  queryError,
  subQueries,
  activeIndex,
  setActiveIndex,
  columns,
  setColumns,
  resourcesData,
  linkedServicesData,
  mainQuery,
  setMainQuery,
  handlers,
  getSubQueryLabel,
  onAddSource,
  isReadOnly = false,
  errors,
  availableSubTypes,
}) => {
  const { fetchedConnectionKeys, editorRef: sharedEditorRef } =
    useRuleResourceContext();
  const { showToast } = useToast();
  const focusedRef = useRef<any>(null);
  const mainEditorRef = useRef<any>(null);
  const [fetchLoading, setFetchLoading] = React.useState<boolean>(false);
  // Which main-content layout to show: the focused editor or the card grid.
  const [viewMode, setViewMode] = useState<"editor" | "grid">("editor");
  // Sources whose read-only reference card is pinned on top while editing.
  const [refIds, setRefIds] = useState<number[]>([]);
  // Index of the sub-query pending deletion; drives the confirmation dialog.
  const [itemToDeleteIndex, setItemToDeleteIndex] = useState<number | null>(
    null,
  );
  // Index of the sub-query pending deactivation; drives its confirmation dialog.
  const [itemToDeactivateIndex, setItemToDeactivateIndex] = useState<
    number | null
  >(null);
  // Pop-out editor: null = closed; >= 0 targets a sub-query, -1 targets Main Query.
  const [popOutIndex, setPopOutIndex] = useState<number | null>(null);

  const confirmRemoveSubQuery = () => {
    if (itemToDeleteIndex !== null) {
      handlers.removeSubQuery(itemToDeleteIndex);
    }
    setItemToDeleteIndex(null);
  };

  // Confirm before deactivating an active sub-query; activating is immediate.
  const requestToggleActive = (index: number) => {
    if (subQueries[index]?.is_active !== false) {
      setItemToDeactivateIndex(index);
    } else {
      handlers.handleToggleActive(index);
    }
  };

  const confirmDeactivateSubQuery = () => {
    if (itemToDeactivateIndex !== null) {
      handlers.handleToggleActive(itemToDeactivateIndex);
    }
    setItemToDeactivateIndex(null);
  };
  // -1 selects the Report Query editor in the sidebar.
  const selected = activeIndex >= 0 ? activeIndex : -1;
  const item = selected >= 0 ? subQueries[selected] : null;

  // Never show the currently-edited source as its own reference card.
  const referenceItems = subQueries.filter(
    (sq, idx) => refIds.includes(sq.id) && idx !== selected,
  );

  const toggleRef = (id: number) => {
    setRefIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  // Drop reference selections for sources that no longer exist.
  useEffect(() => {
    setRefIds((prev) =>
      prev.filter((id) => subQueries.some((sq) => sq.id === id)),
    );
  }, [subQueries]);

  const handleColumnSelect = (column: string) => {
    const ref = selected >= 0 ? focusedRef : mainEditorRef;
    const editor = ref?.current?.editor || sharedEditorRef?.current?.editor;
    if (!editor) return;
    const pos = editor.getCursorPosition();
    const text = `"${column}"`;
    editor.session.insert(pos, text);
    editor.moveCursorToPosition({
      row: pos.row,
      column: pos.column + text.length,
    });
    editor.clearSelection();
    editor.focus();
  };

  // Inserts $$VAR$$ at the cursor of the focused editor (sub-query or Main Query),
  // mirroring the legacy handleVariableSelect behaviour.
  const handleVariableSelect = (variable: string) => {
    const ref = selected >= 0 ? focusedRef : mainEditorRef;
    const editor = ref?.current?.editor || sharedEditorRef?.current?.editor;
    if (!editor) return;
    const pos = editor.getCursorPosition();
    const text = `$$${variable}$$ `;
    editor.session.insert(pos, text);
    editor.moveCursorToPosition({
      row: pos.row,
      column: pos.column + text.length,
    });
    editor.clearSelection();
    editor.focus();
  };

  const handleFetchColumns = async (index: number) => {
    const it = subQueries[index];
    if (!(it?.connection_key_id && it?.linked_service_id && it?.table_name)) {
      showToast(
        "Please fill all external fields before fetching columns",
        "error",
      );
      return;
    }
    try {
      setFetchLoading(true);
      const response = await getTableColumns({
        payload: {
          linked_service_id: String(it?.linked_service_id),
          linked_service_code: it?.linked_service_code,
          connection_key_id: it?.connection_key_id,
          table_name: it?.table_name,
        },
      });
      if (response) {
        const raw = (Object.values(response)[0] as string[]) ?? [];
        const formatted = raw.map((c) => ({ name: c, value: c }));
        setColumns((prev: any) => ({
          ...prev,
          fetchedColumns: [{ name: it?.table_name || "", columns: formatted }],
        }));
        handlers.handleSetExternalColumns(index, formatted);
        showToast("Table columns fetched successfully", "success");
      } else {
        showToast("Cannot fetch table columns", "error");
      }
    } catch (err) {
      showToast("Cannot fetch table columns", "error");
    } finally {
      setFetchLoading(false);
    }
  };

  // Context-scoped columns chips: Resource cards show only their resource's
  // columns; External cards show only their fetched table columns; comparison
  // sub-types show the rule-derived merged/missing/mismatched columns.
  const renderColumnsChipsFor = (item: IMixedSubQueryV2, index: number) => {
    const isExternal = item?.sub_type === "External";
    const isMerge = !!item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type);
    let scopedColumns: any;
    if (isMerge) {
      scopedColumns = buildMergeScopedColumns(item, columns);
    } else if (isExternal) {
      scopedColumns = buildExternalScopedColumns(item);
    } else {
      scopedColumns = buildResourceScopedColumns(item, columns);
    }
    return (
      <ResourceColumnsChips
        key={index}
        handleColumnSelect={handleColumnSelect}
        columns={scopedColumns}
        isFinalQuery={false}
        dialogType="mixed"
        queryBuilderType={
          isMerge
            ? "merged_query"
            : isExternal
              ? "external_query"
              : "resource_query"
        }
        mergeQueryType={
          isMerge ? item.sub_type : isExternal ? "External" : "Resource"
        }
      />
    );
  };

  // Main Query panel chips: aggregate ALL sub-queries.
  const mainQueryColumnsChips = (
    <ResourceColumnsChips
      handleColumnSelect={handleColumnSelect}
      columns={buildMainQueryScopedColumns(subQueries, columns)}
      isFinalQuery={false}
      dialogType="mixed"
      queryBuilderType="main_query"
      mergeQueryType="Resource"
    />
  );

  // Available Variables + Query Templates side panel (matches the legacy tab).
  const variablesPanel = (
    <>
      <Box className="available-box" sx={{ marginBottom: 1 }}>
        <AvailableVariables
          error={errors}
          setErrors={() => null}
          handleVariableSelect={handleVariableSelect}
        />
      </Box>
      <Box sx={{ marginBottom: 1 }}>
        <QueryTemplatesCard
          templates={[
            ...GENERIC_RESOURCE_QUERY_TEMPLATES,
            ...GENERIC_EXTERNAL_QUERY_TEMPLATES,
          ]}
          title="Query Templates"
        />
      </Box>
    </>
  );

  return (
    <EnhancedQueryDialog
      open={open}
      onClose={onClose}
      fullScreen
      hasUnsavedChanges={false}
      className="mixed-v2-dialog"
    >
      {/* Header: query name + Save/Cancel */}
      <Box className="mixed-v2-dialog__header">
        <TextField
          name="name"
          label={
            <span>
              Query Name
              <span className="required-asterisk">*</span>
            </span>
          }
          type="text"
          variant="outlined"
          value={queryName || ""}
          onChange={(e) => setQueryName(e.target.value)}
          error={!!queryError?.name}
          helperText={queryError?.name || ""}
          className={`mixed-v2-dialog__name form-control-autocomplete ${
            queryError?.name ? "has-error" : ""
          }`}
        />
        <Box className="mixed-v2-dialog__header-actions">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_e, value) => value && setViewMode(value)}
            className="mixed-v2-dialog__view-switch"
          >
            <ToggleButton value="editor">
              <ViewSidebarIcon fontSize="small" />
              Expanded Editor
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon fontSize="small" />
              Card Grid
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            color="secondary"
            className="btn-orange btn-border"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className="btn-orange no-transform"
            onClick={onSave}
          >
            Save
          </Button>
        </Box>
      </Box>

      {viewMode === "grid" ? (
        <Box className="mixed-v2-dialog__grid-view">
          <MixedQueryV2Content
            subQueries={subQueries}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            columns={columns}
            setColumns={setColumns}
            resourcesData={resourcesData}
            linkedServicesData={linkedServicesData}
            mainQuery={mainQuery}
            setMainQuery={setMainQuery}
            handlers={handlers}
            getSubQueryLabel={getSubQueryLabel}
            errors={errors}
            isReadOnly={isReadOnly}
            availableSubTypes={availableSubTypes}
            onExpand={(index) => {
              setActiveIndex(index);
              setViewMode("editor");
            }}
            onPopOut={(index) => setPopOutIndex(index)}
            onMainQueryPopOut={() => setPopOutIndex(-1)}
            onAddSource={onAddSource}
          />
        </Box>
      ) : (
        <Box className="mixed-v2-dialog__body">
          {/* Sidebar: check to pin as reference, click a name to edit it */}
          <Box className="mixed-v2-dialog__sidebar">
            <Box className="mixed-v2-dialog__sidebar-head">
              <Typography className="mixed-v2-dialog__sidebar-title">
                Data Sources
              </Typography>
              {onAddSource && (
                <Tooltip title="Data Source" placement="top" arrow>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon fontSize="small" />}
                    className="mixed-v2-dialog__add-btn"
                    onClick={onAddSource}
                  >
                    Add
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Typography className="mixed-v2-dialog__sidebar-hint">
              Select sources to reference while you write.
            </Typography>
            {subQueries.map((sq, idx) => (
              <Box
                key={sq.id}
                className={`mixed-v2-dialog__source-item ${
                  selected === idx ? "is-active" : ""
                } ${sq.is_active === false ? "is-inactive" : ""}`}
              >
                <Checkbox
                  size="small"
                  checked={refIds.includes(sq.id)}
                  onChange={() => toggleRef(sq.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Box
                  className="mixed-v2-dialog__source-label"
                  onClick={() => setActiveIndex(idx)}
                  role="button"
                >
                  <Tooltip
                    title={getSubQueryLabel(sq, idx)}
                    placement="top"
                    arrow
                  >
                    <Typography className="mixed-v2-dialog__source-name">
                      {getSubQueryLabel(sq, idx)}
                    </Typography>
                  </Tooltip>
                  <Chip size="small" label={`Source ${idx + 1}`} />
                </Box>
              </Box>
            ))}
            <Box className="mixed-v2-dialog__source-separator" />
            <Box
              className={`mixed-v2-dialog__source-item ${
                selected === -1 ? "is-active" : ""
              }`}
              onClick={() => setActiveIndex(-1)}
              role="button"
            >
              <Typography className="mixed-v2-dialog__source-name">
                Main Query
              </Typography>
            </Box>
          </Box>

          {/* Main area: reference cards on top, active editor at the bottom */}
          <Box className="mixed-v2-dialog__main">
            {referenceItems.length > 0 && (
              <Box className="mixed-v2-dialog__references">
                {referenceItems.map((ref) => {
                  const refIdx = subQueries.findIndex((s) => s.id === ref.id);
                  return (
                    <SourceReferenceCard
                      key={ref.id}
                      item={ref}
                      index={refIdx}
                      label={getSubQueryLabel(ref, refIdx)}
                      columnNames={getColumnNamesForSource(ref, columns)}
                      onColumnClick={handleColumnSelect}
                      getDependencyDisplayLabel={
                        handlers.getDependencyDisplayLabel
                      }
                    />
                  );
                })}
              </Box>
            )}

            <Box className="mixed-v2-dialog__editor-area">
              {/* <Box className="mixed-v2-dialog__main-head">
              <Typography className="mixed-v2-dialog__main-title">
                {selected >= 0 && item
                  ? getSubQueryLabel(item, selected)
                  : "Main Query"}
              </Typography>
            </Box> */}

              {selected >= 0 && item ? (
                <SubQueryCard
                  item={item}
                  index={selected}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  resourcesData={resourcesData}
                  linkedServicesData={linkedServicesData}
                  connectionKeys={fetchedConnectionKeys || []}
                  availableSubTypes={availableSubTypes}
                  onTypeChange={handlers.handleTypeChange}
                  onFieldChange={handlers.handleFieldChange}
                  onLinkedServiceChange={handlers.handleLinkedServiceChange}
                  onQueryChange={handlers.handleQueryChange}
                  onEditorFocus={handlers.handleEditorFocus}
                  onEditorBlur={handlers.handleEditorBlur}
                  onFetchColumns={handleFetchColumns}
                  onRemove={(i) => setItemToDeleteIndex(i)}
                  onToggleActive={requestToggleActive}
                  onPopOut={(i) => setPopOutIndex(i)}
                  getDependencyDisplayLabel={handlers.getDependencyDisplayLabel}
                  fetchColumnsLoading={fetchLoading}
                  editorRef={focusedRef}
                  columnsChipsSlot={renderColumnsChipsFor(item, selected)}
                  variablesSlot={variablesPanel}
                  referenceLabel={getSubQueryLabel(item, selected)}
                  layout="expanded"
                />
              ) : (
                <Box className="mixed-v2-dialog__report">
                  <Box className="mixed-v2-dialog__report-editor">
                    <MixedSqlEditor
                      value={mainQuery}
                      onChange={setMainQuery}
                      editorStateKey="mixed-v2-dialog-main-query"
                      label="Main Query"
                      error={errors?.[-1] || {}}
                      setErrors={() => null}
                      showAvailableVariables={false}
                      showResourceColumnsChips={false}
                      editorRef={mainEditorRef}
                      onPopOut={() => setPopOutIndex(-1)}
                    />
                  </Box>
                  <Box className="mixed-v2-dialog__report-side-panel">
                    <Box className="mixed-v2-dialog__report-vars">
                      {variablesPanel}
                    </Box>
                    <Box className="mixed-v2-dialog__report-columns">
                      {mainQueryColumnsChips}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <ConfirmDailog
        isDailogOpen={itemToDeleteIndex !== null}
        handleCancelAction={() => setItemToDeleteIndex(null)}
        handleConfirmAction={confirmRemoveSubQuery}
        dailogTitle="Remove Data Source"
        dailogDescription="Are you sure you want to remove this data source? This action cannot be undone."
      />

      <ConfirmDailog
        isDailogOpen={itemToDeactivateIndex !== null}
        handleCancelAction={() => setItemToDeactivateIndex(null)}
        handleConfirmAction={confirmDeactivateSubQuery}
        dailogTitle="Deactivate Data Source"
        dailogDescription="Are you sure you want to deactivate this data source? It will be excluded from the main query and other sub-queries' dependencies until reactivated."
      />

      {popOutIndex !== null && (
        <DynamicSubQueryEditorDialog
          open={popOutIndex !== null}
          onClose={() => setPopOutIndex(null)}
          targetIndex={popOutIndex}
          subQueries={subQueries}
          columns={columns}
          mainQuery={mainQuery}
          title={
            popOutIndex < 0
              ? "Main Query"
              : getSubQueryLabel(subQueries[popOutIndex], popOutIndex)
          }
          isReadOnly={isReadOnly}
          error={errors?.[popOutIndex] || {}}
          onSaveSubQuery={handlers.handleQueryChange}
          onSaveMainQuery={setMainQuery}
        />
      )}
    </EnhancedQueryDialog>
  );
};

export default MixedQueryEditorDialog;
